import { ROUTE_BASE_NAME } from '~/constants'
import { SUPPORTED_SEARCH_QUERY_PARAMETERS } from '~/constants/query-parameters'

export default function useDownloadApp() {
  const { isAndroid, isIos, isMacOS } = useDevice()
  const runtimeConfig = useRuntimeConfig()
  const utmCookie = useUtmParametersCookie()
  const rtsessionid = useRtsessionIdCookie()
  const { userExists, userIsOwner } = useUser()
  const route = useRoute()
  const { routeBaseName } = useBaseName()
  const searchData = useSearchData()

  const playStoreUrl = computed(() => {
    const { utms } = parseTrackingParameters(
      {
        utms: new URLSearchParams(utmCookie.value ?? ''),
        rtsessionid: rtsessionid.value,
        isDeepLinkRequest: true,
        addRtsessionid: true,
      },
    )

    let playStoreUrl
      = runtimeConfig.public.playStoreUrl

    if (Object.keys(utms).length) {
      playStoreUrl += `?${new URLSearchParams(utms)}`
    }

    return playStoreUrl
  })

  const iTunesUrl = computed(() => {
    const { utms } = parseTrackingParameters(
      {
        utms: new URLSearchParams(utmCookie.value ?? ''),
        rtsessionid: rtsessionid.value,
        isDeepLinkRequest: true,
        addRtsessionid: true,
      },
    )

    let iTunesUrl = runtimeConfig.public.appStoreUrl

    if (Object.keys(utms).length) {
      iTunesUrl += `?${new URLSearchParams(utms)}`
    }

    return iTunesUrl
  })

  const trackAndroid = () => {
    trackExternalLinkClicked({
      externalLink: playStoreUrl.value,
      cta: 'Download the app',
      pageSource: routeBaseName.value,
      isOwner: userExists.value ? userIsOwner.value : null,
    })
  }

  const trackiOs = () => {
    trackExternalLinkClicked({
      externalLink: iTunesUrl.value,
      cta: 'Download the app',
      pageSource: routeBaseName.value,
      isOwner: userExists.value ? userIsOwner.value : null,
    })
  }

  const downloadApp = async (params?: { pageSource?: string, platform?: string }) => {
    const { pageSource = routeBaseName.value, platform } = params ?? {}

    if (platform === 'android') {
      trackAndroid()

      await navigateTo(playStoreUrl.value, {
        open: {
          target: '_blank',
        },
      })
    }

    if (platform === 'ios') {
      trackiOs()

      await navigateTo(iTunesUrl.value, {
        open: {
          target: '_blank',
        },
      })
    }

    const { utms } = parseTrackingParameters(
      {
        utms: new URLSearchParams(utmCookie.value ?? ''),
        rtsessionid: rtsessionid.value,
        isDeepLinkRequest: true,
        addRtsessionid: true,
      },
    )

    trackAppBannerClicked({
      pageSource: routeBaseName.value,
      os: isIos ? 'ios' : 'android',
      cta: 'bannerClicked',
    })

    if (isAndroid) {
      trackAndroid()
    }
    else if (isIos) {
      trackiOs()
    }

    if (rtsessionid.value && rtsessionid.value !== 'null' && isMetaClient) {
      const { protocol, url } = runtimeConfig.public.appsFlyer
      const appsFlyerLink = `${url}?deep_link_value=`
      let base = protocol

      let queryParams = new URLSearchParams([])
      const utmParams = new URLSearchParams(utms)

      // We also pass the full query string to the dynamic link endpoint, but
      // we have to make sure of the following: If there's NO SearchAddress
      // and NO bounding box, we need to manually add the SearchAddress from
      // the search plugin location.
      //
      // This should only happen on the search page and on a listing page.
      if (pageSource === ROUTE_BASE_NAME.rv_search || pageSource === ROUTE_BASE_NAME.rv_rental) {
        const url = window.location.href
        const urlObj = new URL(url)
        queryParams = new URLSearchParams(urlObj.search)

        let hasSearchAddress = false
        let hasBoundingBox = false

        // Check if SearchAddress OR a bounding box is provided.
        for (const [key] of queryParams) {
          if (key === SUPPORTED_SEARCH_QUERY_PARAMETERS.SearchAddress) {
            hasSearchAddress = true
          }
          if (
            key === SUPPORTED_SEARCH_QUERY_PARAMETERS.NeLat
            || key === SUPPORTED_SEARCH_QUERY_PARAMETERS.NeLng
            || key === SUPPORTED_SEARCH_QUERY_PARAMETERS.SwLat
            || key === SUPPORTED_SEARCH_QUERY_PARAMETERS.SwLng
          ) {
            hasBoundingBox = true
          }
        }

        // Set the search address if it's not provided, but only on the search page.
        if (!hasSearchAddress && !hasBoundingBox && pageSource === ROUTE_BASE_NAME.rv_search) {
          const searchAddress = searchData.value?.location?.address ?? ''
          queryParams.set('SearchAddress', searchAddress)
        }

        // We also set the `SearchLat` and `SearchLng` as parameters, because
        // those are the values needed for the unified search endpoint in case
        // of a search address.
        if (!hasBoundingBox && pageSource === ROUTE_BASE_NAME.rv_search) {
          const searchLat = searchData.value?.location?.coordinates?.lat?.toString()
          const searchLng = searchData.value?.location?.coordinates?.lng?.toString()

          if (searchLat && searchLng) {
            queryParams.set('SearchLat', searchLat)
            queryParams.set('SearchLng', searchLng)
          }
        }
      }

      // Append the path as the deep link URL.
      // Exception: `index`, which should not be appended.
      if (pageSource !== ROUTE_BASE_NAME.index) {
        let path = route.path.substring(1)

        // If the path starts with `host`, we need to replace it with `dashboard`
        // because the deep link URL is not the same as the path.
        // See dashboardMode for more details.
        if (path.startsWith('host')) {
          path = path.replace('host', 'dashboard')
        }

        base += path
      }

      // Combine the provided query params with the optional utm params.
      for (const [key, value] of utmParams) {
        queryParams.append(key, value)
      }

      // Add the (dev) env to the params.
      queryParams.append('rvezy_env', runtimeConfig.public.environment.toLowerCase())

      // Create the final query string.
      const params = new URLSearchParams(queryParams).toString()

      const encodedParams = encodeURIComponent(`${base}?${params}`)
      const link = `${appsFlyerLink}${encodedParams}`

      await navigateTo(link, {
        open: {
          target: '_blank',
        },
      })
    }
    else if (isAndroid) {
      await navigateTo(playStoreUrl.value, {
        open: {
          target: '_blank',
        },
      })
    }
    else if (isMacOS) {
      await navigateTo(iTunesUrl.value, {
        open: {
          target: '_blank',
        },
      })
    }
    else {
      await navigateTo('/')
    }
  }

  return {
    playStoreUrl,
    iTunesUrl,
    trackAndroid,
    trackiOs,
    downloadApp,
  }
}
